import React from 'react';
import { Leaf } from 'lucide-react';
import { getMarkerStatus } from './SensorMap'; // Import the shared function

const SensorLocations = ({ sensors, onSensorSelect, selectedSensor, lastReadings = {} }) => {
  const sensorsBySpecies = sensors.reduce((acc, sensor) => {
    if (!acc[sensor.species]) {
      acc[sensor.species] = [];
    }
    acc[sensor.species].push(sensor);
    return acc;
  }, {});

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-blue-500';
      case 'delayed':
        return 'bg-yellow-500';
      case 'inactive':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getLastReadingTimestamp = (sensor) => {
    const lastReading = lastReadings[sensor.id];
    if (!lastReading) return 'No data';

    const timestamp = new Date(lastReading.timestamp);
    const now = new Date();
    const minutesDiff = Math.floor((now - timestamp) / (1000 * 60));

    if (minutesDiff < 60) {
      return `${minutesDiff}m ago`;
    } else if (minutesDiff < 1440) {
      const hours = Math.floor(minutesDiff / 60);
      return `${hours}h ago`;
    } else {
      const days = Math.floor(minutesDiff / 1440);
      return `${days}d ago`;
    }
  };

  const speciesCount = Object.keys(sensorsBySpecies).length;

  return (
    <div className="mb-6">
      <h2 className="text-2xl font-semibold mb-3 text-green-700">Available Sensors</h2>
      
      <div className={`grid grid-cols-1 md:grid-cols-2 ${
        speciesCount === 2 ? 'lg:grid-cols-2 max-w-4xl mx-auto' : 'lg:grid-cols-3'
      } gap-4`}>
        {Object.entries(sensorsBySpecies).map(([species, speciesSensors]) => (
          <div 
            key={species}
            className="bg-white rounded-lg shadow-md overflow-hidden"
          >
            <div className="bg-green-50 p-3 border-b border-green-100">
              <div className="flex items-center">
                <Leaf size={20} className="text-green-600 mr-2" />
                <h3 className="text-lg font-medium text-green-800">{species}</h3>
                <span className="ml-auto text-sm text-green-600">
                  {speciesSensors.length} {speciesSensors.length === 1 ? 'sensor' : 'sensors'}
                </span>
              </div>
            </div>

            <div className="p-3 space-y-2">
              {speciesSensors.map((sensor) => {
                const status = getMarkerStatus(sensor, lastReadings); // Use the shared function
                const lastReading = lastReadings[sensor.id];
                return (
                  <button
                    key={sensor.id}
                    onClick={() => onSensorSelect(sensor)}
                    className={`w-full flex items-center justify-between p-2 rounded-md text-left transition-colors duration-200 ${
                      selectedSensor?.id === sensor.id
                        ? 'bg-green-100 text-green-800 hover:bg-green-200'
                        : 'hover:bg-gray-50 text-gray-700 hover:text-green-700'
                    }`}
                  >
                    <div className="flex items-center flex-grow">
                      <div 
                        className={`w-3 h-3 mr-3 rounded-full ${getStatusColor(status)} 
                          ${selectedSensor?.id === sensor.id ? 'ring-2 ring-green-200 ring-offset-2' : ''}`}
                      />
                      <div className="flex-grow">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{sensor.id}</span>
                          <span className="text-sm text-gray-500 ml-2">
                            {getLastReadingTimestamp(sensor)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500">
                          {sensor.diameter} diameter • {sensor.interval}min interval
                        </div>
                        {lastReading && (
                          <div className="text-xs text-gray-600 mt-1">
                            {lastReading.uncorrected_inner && (
                              <span className="mr-3">
                                Inner: {lastReading.uncorrected_inner.toFixed(2)} cm/hr
                              </span>
                            )}
                            {lastReading.uncorrected_outer && (
                              <span>
                                Outer: {lastReading.uncorrected_outer.toFixed(2)} cm/hr
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center justify-end space-x-4 text-sm text-gray-600">
        <div className="flex items-center space-x-1">
          <div className="w-2.5 h-2.5 bg-blue-500 rounded-full"></div>
          <span>Active</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-2.5 h-2.5 bg-yellow-500 rounded-full"></div>
          <span>Delayed</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-2.5 h-2.5 bg-red-500 rounded-full"></div>
          <span>Inactive</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-2.5 h-2.5 bg-gray-500 rounded-full"></div>
          <span>No Data</span>
        </div>
      </div>

      <p className="mt-4 text-sm text-gray-500 italic">
        Select a sensor to view detailed sap flow measurements and health data.
      </p>
    </div>
  );
};

export default SensorLocations;